import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { organizationService } from '../services/organizationService';

const normalizePlanTierLabel = (planTier?: string): string => {
  const normalized = (planTier || '').trim().toUpperCase();
  return normalized === 'FREE' ? 'BASIC' : normalized || 'N/A';
};

const OrganizationListPage: React.FC = () => {
  const navigate = useNavigate();

  const {
    data: organizations = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['organizations-list'],
    queryFn: () => organizationService.getOrganizations(),
    staleTime: 2 * 60 * 1000,
  });

  return (
    <Box
      sx={{
        maxWidth: { xs: 400, sm: 768, md: 1000, lg: 1200 },
        mx: 'auto',
        px: { xs: 2, sm: 3, md: 4 },
        py: 3,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Admin {'>'} Configuration {'>'} Organizations
      </Typography>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={1.5}
        mb={2.5}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Organization Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select an organization to manage on a dedicated screen.
          </Typography>
        </Box>

        <Button variant="contained" startIcon={<AddIcon />} disabled>
          Create Organization
        </Button>
      </Box>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={5}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load organizations: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      )}

      {!isLoading && !error && organizations.length === 0 && (
        <Alert severity="info">No organizations found.</Alert>
      )}

      {!isLoading && !error && organizations.length > 0 && (
        <Stack spacing={1.5}>
          {organizations.map((org) => (
            <Card key={org.id} variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1.5} justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                    <BusinessIcon color="primary" />
                    <Box minWidth={0}>
                      <Typography variant="subtitle1" fontWeight={700} noWrap>
                        {org.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {org.slug || org.id} • {normalizePlanTierLabel(org.planTier)} Plan
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(`/organization/${org.id}`)}
                  >
                    Open
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default OrganizationListPage;